/**
 * Low-data mode.
 *
 * Recipients and merchants in Haiti are frequently on 3G or a metered bundle.
 * This lets them stop images loading automatically, replacing each with a
 * placeholder they can tap to load on demand.
 *
 * ## What it must NOT do
 *
 * The handoff's rule is that all codes and status updates stay reachable. The
 * delivery proof photo is not decoration — it is how a sender 1,500 miles away
 * verifies the goods actually arrived, and it is the evidence in a dispute. So
 * low-data mode never REMOVES a photo; it defers it behind an explicit tap,
 * with the file size shown so the choice is informed.
 *
 * ## Why a preference and not a network check
 *
 * `navigator.connection` reports the radio type, not what the data costs. A
 * user on 4G with 200MB left for the month wants this on; a user on slow wifi
 * does not. Only they know. We default OFF and let them choose, and remember
 * it per device in localStorage — it is a display preference, not account
 * state, and a shared family phone should not inherit it from the server.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'voyelakay.lowData';

interface LowDataValue {
  /** True when images should be deferred behind a tap. */
  lowData: boolean;
  setLowData: (on: boolean) => void;
  toggle: () => void;
}

const LowDataCtx = createContext<LowDataValue | null>(null);

export function LowDataProvider({ children }: { children: ReactNode }) {
  const [lowData, setLowDataState] = useState<boolean>(() => {
    // localStorage can throw in private mode or with site data blocked, and a
    // display preference must never break the app.
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lowData ? '1' : '0');
    } catch {
      // Preference simply will not persist. The session still works.
    }
  }, [lowData]);

  const setLowData = useCallback((on: boolean) => setLowDataState(on), []);
  const toggle = useCallback(() => setLowDataState((v) => !v), []);

  return (
    <LowDataCtx.Provider value={{ lowData, setLowData, toggle }}>
      {children}
    </LowDataCtx.Provider>
  );
}

export function useLowData(): LowDataValue {
  const ctx = useContext(LowDataCtx);
  // Default to OFF rather than throwing: a component rendered outside the
  // provider should show images normally, not crash.
  return ctx ?? { lowData: false, setLowData: () => {}, toggle: () => {} };
}
